import { createBrowserRouter, Navigate } from 'react-router-dom'
import { RootLayout } from './layouts/RootLayout'
import { DashboardLayout } from './layouts/DashboardLayout'
import { ProtectedRoute } from './components/common/ProtectedRoute'
import { DashboardRedirect } from './components/common/DashboardRedirect'
import { FeatureFlag } from './components/common/FeatureFlag'
import { FeatureFlags } from './types/feature-flags'

// Pages
import { HomePage } from './pages/HomePage'
import { SignInPage } from './pages/SignInPage'
import { SignUpPage } from './pages/SignUpPage'
import { AuthCallbackPage } from './pages/AuthCallbackPage'
import { WaitlistPage } from './pages/WaitlistPage'
import { OnboardingLayout } from './onboarding/OnboardingLayout'
import { ONBOARDING_STEPS } from './onboarding/flow.config'
import { PlaygroundPage } from './pages/PlaygroundPage'
import { EvalPage } from './pages/EvalPage'
import { EvalRunDetailPage } from './pages/EvalRunDetailPage'
import { EvalCaseDetailPage } from './pages/EvalCaseDetailPage'
import { EvalResultDetailPage } from './pages/EvalResultDetailPage'

// Dashboard pages
import { ReportingPage } from './pages/dashboard/ReportingPage'
import { VoicePipelinePage } from './pages/dashboard/VoicePipelinePage'
import { ActivityPage } from './pages/dashboard/ActivityPage'
import { UsersPage } from './pages/dashboard/UsersPage'
import { UserProfilePage } from './pages/dashboard/UserProfilePage'
import { UserSecurityPage } from './pages/dashboard/UserSecurityPage'
import { UserSessionsPage } from './pages/dashboard/UserSessionsPage'
import { OrganizationSwitcherPage } from './pages/dashboard/OrganizationSwitcherPage'

// Platform pages (Public API traffic, super admin)
import { PlatformInvocationsPage } from './pages/platform/PlatformInvocationsPage'
import { PlatformDeliveriesPage } from './pages/platform/PlatformDeliveriesPage'
import { PlatformSubscriptionsPage } from './pages/platform/PlatformSubscriptionsPage'
import { ApiUsagePage } from './pages/platform/ApiUsagePage'

// Settings pages
import { CompanySettingsPage } from './pages/settings/CompanySettingsPage'
import { ChannelSettingsPage } from './pages/settings/ChannelSettingsPage'
import { ApiKeysSettingsPage } from './pages/settings/ApiKeysSettingsPage'
import { BillingSettingsPage } from './pages/settings/BillingSettingsPage'
import { IntegrationsSettingsPage } from './pages/settings/IntegrationsSettingsPage'
import { PropertyMeldIntegrationPage } from './pages/settings/integrations/PropertyMeldIntegrationPage'
import { ShowMojoIntegrationPage } from './pages/settings/integrations/ShowMojoIntegrationPage'
import { TenantTurnerIntegrationPage } from './pages/settings/integrations/TenantTurnerIntegrationPage'
import { RentManagerIntegrationPage } from './pages/settings/integrations/RentManagerIntegrationPage'
import { BuildingLinkIntegrationPage } from './pages/settings/integrations/BuildingLinkIntegrationPage'
import { BuildiumIntegrationPage } from './pages/settings/integrations/BuildiumIntegrationPage'
import { HubSpotIntegrationPage } from './pages/settings/integrations/HubSpotIntegrationPage'
import { AppFolioIntegrationPage } from './pages/settings/integrations/AppFolioIntegrationPage'
import { YardiVoyagerIntegrationPage } from './pages/settings/integrations/YardiVoyagerIntegrationPage'
import { YardiBreezeIntegrationPage } from './pages/settings/integrations/YardiBreezeIntegrationPage'
import { EpicPmIntegrationPage } from './pages/settings/integrations/EpicPmIntegrationPage'
import { EntrataIntegrationPage } from './pages/settings/integrations/EntrataIntegrationPage'
import { RentvineIntegrationPage } from './pages/settings/integrations/RentvineIntegrationPage'
import { DoorLoopIntegrationPage } from './pages/settings/integrations/DoorLoopIntegrationPage'
import { DirectorySettingsPage } from './pages/settings/DirectorySettingsPage'
import { TeamSettingsPage } from './pages/settings/TeamSettingsPage'
import RoleDetailRoute from './pages/settings/team/RoleDetailRoute'
import { PartnersSettingsPage } from './pages/settings/PartnersSettingsPage'
import { PartnerActivityPage } from './pages/partner/PartnerActivityPage'

// Agents pages
import { AgentsListPage } from './pages/agents/AgentsListPage'
import { AgentDetailPage } from './pages/agents/AgentDetailPage'
import { AgentsOutboundPage } from './pages/agents/AgentsOutboundPage'
import { AgentsVoicePage } from './pages/agents/AgentsVoicePage'

// Knowledge & Actions pages (now top-level)
import { AgentsKnowledgePage } from './pages/agents/AgentsKnowledgePage'
import { AgentsActionsPage } from './pages/agents/AgentsActionsPage'
import { HandoffRulePage } from './pages/actions/HandoffRulePage'
import { ActionsPage } from './pages/node-actions/ActionsPage'
import { ActionDetailPage } from './pages/node-actions/ActionDetailPage'
import { ActionGroupDetailPage } from './pages/node-actions/ActionGroupDetailPage'
import { ScenariosPage } from './pages/scenarios/ScenariosPage'
import { ScenarioDetailPage } from './pages/scenarios/ScenarioDetailPage'
import { NodeAgentsListPage } from './pages/node-agents/NodeAgentsListPage'
import { NodeAgentPage } from './pages/node-agents/NodeAgentPage'
import { TriggersPage } from './pages/triggers/TriggersPage'
import { TriggerDetailPage } from './pages/triggers/TriggerDetailPage'

// Partner pages
import { PartnerCredentialsPage } from './pages/partner/PartnerCredentialsPage'
import { PartnerCompaniesPage } from './pages/partner/PartnerCompaniesPage'

import { NotFoundPage } from './pages/NotFoundPage'

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <NotFoundPage />,
    children: [
      // Public routes
      { index: true, element: <HomePage /> },
      { path: 'sign-in', element: <SignInPage /> },
      { path: 'sign-up', element: <SignUpPage /> },
      { path: 'auth/callback', element: <AuthCallbackPage /> },

      {
        path: 'onboarding',
        element: (
          <ProtectedRoute>
            <FeatureFlag
              flag={FeatureFlags.SELF_SERVICE_ONBOARDING}
              fallback={<Navigate to="/waitlist" replace />}
            >
              <OnboardingLayout />
            </FeatureFlag>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="setup" replace /> },
          ...ONBOARDING_STEPS.map(step => ({
            path: step.route.slice('/onboarding/'.length),
            element: <step.component />,
          })),
        ],
      },
      {
        path: 'waitlist',
        element: (
          <ProtectedRoute>
            <WaitlistPage />
          </ProtectedRoute>
        ),
      },

      // Protected dashboard routes
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <DashboardRedirect />,
          },
          { path: 'reporting', element: <ReportingPage /> },
          {
            path: 'performance',
            element: (
              <ProtectedRoute requireSuperAdmin>
                <VoicePipelinePage />
              </ProtectedRoute>
            ),
          },
          { path: 'activity', element: <ActivityPage /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'user-profile', element: <UserProfilePage /> },
          { path: 'user-security', element: <UserSecurityPage /> },
          { path: 'user-sessions', element: <UserSessionsPage /> },
          {
            path: 'organization-switcher',
            element: <OrganizationSwitcherPage />,
          },
        ],
      },

      // Protected settings routes
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { path: 'company', element: <CompanySettingsPage /> },
          { path: 'channels', element: <ChannelSettingsPage /> },
          {
            path: 'api-keys',
            element: (
              <ProtectedRoute enabledForFeatureFlag={FeatureFlags.OUTBOUND}>
                <ApiKeysSettingsPage />
              </ProtectedRoute>
            ),
          },
          { path: 'billing', element: <BillingSettingsPage /> },
          { path: 'integrations', element: <IntegrationsSettingsPage /> },
          {
            path: 'integrations/property-meld',
            element: <PropertyMeldIntegrationPage />,
          },
          {
            path: 'integrations/showmojo',
            element: <ShowMojoIntegrationPage />,
          },
          {
            path: 'integrations/tenant-turner',
            element: <TenantTurnerIntegrationPage />,
          },
          {
            path: 'integrations/rent-manager',
            element: <RentManagerIntegrationPage />,
          },
          {
            path: 'integrations/building-link',
            element: <BuildingLinkIntegrationPage />,
          },
          {
            path: 'integrations/buildium',
            element: <BuildiumIntegrationPage />,
          },
          {
            path: 'integrations/hubspot',
            element: <HubSpotIntegrationPage />,
          },
          {
            path: 'integrations/appfolio',
            element: <AppFolioIntegrationPage />,
          },
          {
            path: 'integrations/yardi-voyager',
            element: <YardiVoyagerIntegrationPage />,
          },
          {
            path: 'integrations/yardi-breeze',
            element: <YardiBreezeIntegrationPage />,
          },
          {
            path: 'integrations/entrata',
            element: <EntrataIntegrationPage />,
          },
          {
            path: 'integrations/epic-pm',
            element: <EpicPmIntegrationPage />,
          },
          {
            path: 'integrations/rentvine',
            element: <RentvineIntegrationPage />,
          },
          {
            path: 'integrations/doorloop',
            element: <DoorLoopIntegrationPage />,
          },
          { path: 'directory', element: <DirectorySettingsPage /> },
          { path: 'team', element: <TeamSettingsPage /> },
          { path: 'team/new', element: <RoleDetailRoute isNew /> },
          { path: 'team/:roleId', element: <RoleDetailRoute /> },
        ],
      },

      // Partner routes
      {
        path: 'partner',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: 'partners',
            element: (
              <ProtectedRoute requireSuperAdmin>
                <PartnersSettingsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'partners/:id/activity',
            element: (
              <ProtectedRoute requireSuperAdmin>
                <PartnerActivityPage />
              </ProtectedRoute>
            ),
          },
          { path: 'credentials', element: <PartnerCredentialsPage /> },
          { path: 'companies', element: <PartnerCompaniesPage /> },
        ],
      },

      // Protected agents routes
      {
        path: 'agents',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          // New multi-agent list and detail: always show (OUTBOUND flag only affects modal options and Prompt field)
          { index: true, element: <AgentsListPage /> },
          { path: 'voice', element: <AgentsVoicePage /> },
          {
            path: 'outbound',
            element: (
              <FeatureFlag
                flag={FeatureFlags.OUTBOUND}
                fallback={<Navigate to="/agents" replace />}
              >
                <AgentsOutboundPage />
              </FeatureFlag>
            ),
          },
          { path: 'new', element: <AgentDetailPage /> },
          { path: ':id', element: <AgentDetailPage /> },
        ],
      },

      // Knowledge (top-level, shared resource)
      {
        path: 'knowledge',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [{ index: true, element: <AgentsKnowledgePage /> }],
      },

      // Actions (top-level, shared resource)
      {
        path: 'actions',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <AgentsActionsPage /> },
          { path: 'handoff/new', element: <HandoffRulePage /> },
          { path: 'handoff/:id', element: <HandoffRulePage /> },
        ],
      },

      // v3 node-level shared actions (NodeActionGroup rows)
      {
        path: 'node-actions',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <FeatureFlag
                flag={FeatureFlags.NODE_AGENTS}
                fallback={<Navigate to="/agents" replace />}
              >
                <ActionsPage />
              </FeatureFlag>
            ),
          },
          {
            path: 'new',
            element: (
              <FeatureFlag
                flag={FeatureFlags.NODE_AGENTS}
                fallback={<Navigate to="/agents" replace />}
              >
                <ActionDetailPage />
              </FeatureFlag>
            ),
          },
          {
            path: 'groups/new',
            element: (
              <FeatureFlag
                flag={FeatureFlags.NODE_AGENTS}
                fallback={<Navigate to="/agents" replace />}
              >
                <ActionGroupDetailPage />
              </FeatureFlag>
            ),
          },
          {
            path: 'groups/:id',
            element: (
              <FeatureFlag
                flag={FeatureFlags.NODE_AGENTS}
                fallback={<Navigate to="/agents" replace />}
              >
                <ActionGroupDetailPage />
              </FeatureFlag>
            ),
          },
          {
            path: ':id',
            element: (
              <FeatureFlag
                flag={FeatureFlags.NODE_AGENTS}
                fallback={<Navigate to="/agents" replace />}
              >
                <ActionDetailPage />
              </FeatureFlag>
            ),
          },
        ],
      },

      // Scenarios (top-level, behind outbound FF)
      {
        path: 'scenarios',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <FeatureFlag
                flag={FeatureFlags.OUTBOUND}
                fallback={<Navigate to="/agents" replace />}
              >
                <ScenariosPage />
              </FeatureFlag>
            ),
          },
          {
            path: 'new',
            element: (
              <FeatureFlag
                flag={FeatureFlags.OUTBOUND}
                fallback={<Navigate to="/agents" replace />}
              >
                <ScenarioDetailPage />
              </FeatureFlag>
            ),
          },
          {
            path: ':id',
            element: (
              <FeatureFlag
                flag={FeatureFlags.OUTBOUND}
                fallback={<Navigate to="/agents" replace />}
              >
                <ScenarioDetailPage />
              </FeatureFlag>
            ),
          },
        ],
      },

      // Node agents (top-level, behind node-agents FF)
      {
        path: 'node-agents',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <FeatureFlag
                flag={FeatureFlags.NODE_AGENTS}
                fallback={<Navigate to="/agents" replace />}
              >
                <NodeAgentsListPage />
              </FeatureFlag>
            ),
          },
          {
            path: ':id',
            element: (
              <FeatureFlag
                flag={FeatureFlags.NODE_AGENTS}
                fallback={<Navigate to="/agents" replace />}
              >
                <NodeAgentPage />
              </FeatureFlag>
            ),
          },
        ],
      },

      // Triggers (company-scoped; super admins act on the selected company)
      {
        path: 'triggers',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <TriggersPage /> },
          { path: 'new', element: <TriggerDetailPage /> },
          { path: 'events', element: <PlatformInvocationsPage /> },
          { path: ':id', element: <TriggerDetailPage /> },
        ],
      },

      // Webhook usage, subscriptions + delivery log (company-scoped)
      {
        path: 'webhooks',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <ApiUsagePage /> },
          { path: 'endpoints', element: <PlatformSubscriptionsPage /> },
          { path: 'deliveries', element: <PlatformDeliveriesPage /> },
        ],
      },

      // Protected evaluations route (super admin only)
      {
        path: 'evaluations',
        element: (
          <ProtectedRoute requireSuperAdmin>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <EvalPage /> },
          { path: 'cases/:id', element: <EvalCaseDetailPage /> },
          { path: 'runs/:id', element: <EvalRunDetailPage /> },
          {
            path: 'runs/:runId/results/:resultId',
            element: <EvalResultDetailPage />,
          },
        ],
      },

      // Protected playground route (super admin only)
      {
        path: 'playground',
        element: (
          <ProtectedRoute requireSuperAdmin>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [{ index: true, element: <PlaygroundPage /> }],
      },
    ],
  },
])
